import classNames from "classnames";
import qs from "qs";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { withRouter } from "react-router-dom";
import { compose } from "redux";

import withPlans from "../../../../../hocs/withPlans";
import withUserData from "../../../../../hocs/withUserData";
import Loader from "../../../common/Loader";
import PageTitle from "../../../common/PageTitle";
import SquaberTooltip from "../../../common/SquaberTooltip";
import PlanTableCheck from "../../../landingPage/components/LandingTypography/assets/plan_table_check.svg";
import PlanTableX from "../../../landingPage/components/LandingTypography/assets/plan_table_x.svg";
import useIsDesktop from "../../../landingPage/hooks/useIsDesktop";
import useIsScreenXXS from "../../../landingPage/hooks/useIsScreenXXS";
import FormatCurrency from "../../../landingPage/pages/Homepage/components/FormatCurrency";
import LandingTypography from "../../../landingPage/pages/Homepage/components/LandingTypography";
import Bonus from "../../../landingPage/pages/Plans/components/Bonus";
import BonusSquare from "../../../landingPage/pages/Plans/components/BonusSquare";
import Info from "../../../landingPage/pages/Plans/components/Info";
import PaymentBarWrapper from "../../../landingPage/pages/Plans/components/PaymentBarWrapper";
import Period from "../../../landingPage/pages/Plans/components/Period";
import PlanBox from "../../../landingPage/pages/Plans/components/PlanBox";
import PlanTable2 from "../../../landingPage/pages/Plans/components/PlanTable2";
import {
  getAvailablePeriods,
  getAllowPeriodChange,
  getCurrencyOfferByPeriod,
  getDefaultPeriod,
  isPeriodAllowed,
  KNOWN_PERIODS
} from "../../../../../lib/Plans";
import { SUPPORTED_PAYMENT_METHODS } from "../../PaymentForm/modules/default";
import { useIsProceedToPayment } from "../../../landingPage/pages/Plans/utils/useOnPaymentButtonClick";
import withPaymentDetails from "../../../../../hocs/withPaymentDetails";
import DowngradeInfo from "../../../landingPage/pages/Plans/components/DowngradeInfo";
import sortedFeaturesByIncludedFeaturesCount from "../../../landingPage/pages/Plans/utils/sortedFeaturesByIncludedFeaturesCount";
import useAutoSelectHighestPlan from "../hooks/useAutoSelectHighestPlan";

import "../styles/default.scss";
import PlansToggleSelection from "../components/PlansToggleSelection";

const Plans = ({
  translate,
  offers,
  finalizePayment,
  paymentFormLoading,
  userData,
  locale,
  match,
  supportedPaymentMethods = SUPPORTED_PAYMENT_METHODS,
  location,
  paymentDetails,
  loading: paymentDetailsLoading
}) => {
  const [period, setPeriod] = useState(KNOWN_PERIODS.YEARLY);
  const [selectedPlan, setSelectedPlan] = useState(userData?.current_plan);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState();
  const isDesktop = useIsDesktop();
  const isScreenXXS = useIsScreenXXS();
  const isProceedToPayment = useIsProceedToPayment();

  const currentOffer = offers?.data?.offers?.[0];
  const guaranteedUserOffer = offers?.data?.guaranteedUserOffer;
  const hasGuaranteedUserOffer = !!guaranteedUserOffer;
  const hasPlans = currentOffer?.plans?.length > 0;
  const plans = currentOffer?.plans ?? [];
  const availableInPlanId = useMemo(() => {
    return match?.params?.id;
  }, [match]);

  const allowedPeriods = useMemo(
    () => getAvailablePeriods(currentOffer),
    [currentOffer]
  );

  const allowPeriodChange = useMemo(
    () => getAllowPeriodChange(allowedPeriods, paymentDetails),
    [allowedPeriods, paymentDetails]
  );

  const currentCurrencyOfferByPeriod = useMemo(() => {
    return getCurrencyOfferByPeriod({
      currency_offers: selectedPlan?.currency_offers,
      period
    });
  }, [selectedPlan, period]);

  const handleSetPeriod = useCallback(
    nextPeriod => {
      if (allowPeriodChange && typeof nextPeriod === "number") {
        setPeriod(nextPeriod);
      }
    },
    [allowPeriodChange]
  );

  useAutoSelectHighestPlan({
    plans,
    currentPeriod: period,
    selectedPlan,
    setSelectedPlan
  });

  useEffect(() => {
    if (!allowedPeriods.length) {
      return;
    }

    const search = location.search;
    const defaultPeriod = getDefaultPeriod(allowedPeriods);

    if (!search) {
      if (
        hasGuaranteedUserOffer &&
        isPeriodAllowed(guaranteedUserOffer.periods, allowedPeriods)
      ) {
        setPeriod(guaranteedUserOffer.periods);
        return;
      }

      setPeriod(current =>
        isPeriodAllowed(current, allowedPeriods)
          ? current
          : defaultPeriod ?? current
      );
      return;
    }

    const searchParams = qs.parse(search, { ignoreQueryPrefix: true });
    const queryPeriod = Number(searchParams?.period);

    if (isPeriodAllowed(queryPeriod, allowedPeriods)) {
      setPeriod(queryPeriod);
      return;
    }

    setPeriod(current =>
      isPeriodAllowed(current, allowedPeriods)
        ? current
        : defaultPeriod ?? current
    );
  }, [location?.search, offers, allowedPeriods, hasGuaranteedUserOffer]);

  useEffect(() => {
    if (!currentOffer || !hasPlans) {
      return;
    }

    let startingPlan = currentOffer.plans[0];

    if (typeof availableInPlanId !== "undefined") {
      const availableInPlan = currentOffer.plans?.find?.(
        ({ id }) => id === Number(availableInPlanId)
      );

      if (availableInPlanId) {
        startingPlan = availableInPlan;
      }
    } else {
      const recommendedPlan = currentOffer.plans?.find?.(
        ({ is_recommended }) => is_recommended
      );

      if (recommendedPlan) {
        startingPlan = recommendedPlan;
      }
    }

    setSelectedPlan(startingPlan);
  }, [offers, availableInPlanId]);

  if (offers.loading !== false || paymentDetailsLoading !== false) {
    return (
      <div>
        <Loader />
      </div>
    );
  }

  if (!currentOffer && offers.loading === false) {
    return (
      <LandingTypography className={"plans"}>
        <p>{translate("plans_no-offers")}</p>
      </LandingTypography>
    );
  }

  return (
    <LandingTypography className={"plans"}>
      <PageTitle title={translate("plans_page-title")} />
      <header
        className={classNames("flex", {
          "flex-col mb-5": !isDesktop,
          "align-center justify-between mb-14 mt-6.5": isDesktop
        })}
      >
        <h1
          className={classNames("font-medium m-0 text-black", {
            "m-0 text-2xl.1": isDesktop,
            "mt-5 mb-4 text-2xl": !isDesktop
          })}
        >
          {isDesktop
            ? translate("plans_heading_desktop")
            : translate("plans_heading")}
        </h1>
        <div
          className={classNames("flex align-center gap-3", {
            "mb-12": isScreenXXS
          })}
        >
          <strong className={"text-xs font-medium m-0 text-black-4"}>
            {translate("plans_toggle_title")}
          </strong>
          <div>
            <PlansToggleSelection
              hasGuaranteedUserOffer={hasGuaranteedUserOffer}
              period={period}
              allowedPeriods={allowedPeriods}
              allowPeriodChange={allowPeriodChange}
              handleSetPeriod={handleSetPeriod}
            />
          </div>
        </div>
      </header>
      <section>
        <DowngradeInfo paymentDetails={paymentDetails} />
        {isProceedToPayment ? (
          <Loader small />
        ) : (
          <div
            className={classNames({
              "grid gap-4 grid-cols-4 mb-10": isDesktop,
              "flex flex-col gap-3 mb-8": !isDesktop
            })}
          >
            {currentOffer.plans.map(plan => {
              const isCurrentPlan = userData?.current_plan?.id === plan.id;
              return (
                <PlanBox
                  isSelectedPlan={selectedPlan?.id === plan.id}
                  onSelect={() => setSelectedPlan(plan)}
                  period={period}
                  isAvailableInPlan={match?.params?.id === String(plan.id)}
                  plan={plan}
                  key={plan.id}
                  isCurrentPlan={isCurrentPlan}
                  isHighlightedPlan={plan.is_highlighted}
                  isRecommendedPlan={plan.is_recommended}
                  selectedPaymentMethod={selectedPaymentMethod}
                />
              );
            })}
          </div>
        )}
      </section>
      {currentOffer?.bonus ? (
        <section
          className={classNames({
            "mt-15 mb-10": isDesktop,
            "mb-8": !isDesktop
          })}
        >
          {isDesktop ? (
            <Bonus
              locale={locale}
              translate={translate}
              currentOffer={currentOffer}
            />
          ) : (
            <BonusSquare
              locale={locale}
              translate={translate}
              currentOffer={currentOffer}
            />
          )}
        </section>
      ) : null}
      <section
        className={classNames("flex bg-white rounded", {
          "mb-10 pt-6 gap-8 px-7 pb-7 flex-wrap": isDesktop,
          "mb-8 p-4 gap-6 flex-col": !isDesktop
        })}
      >
        {selectedPlan?.included_features?.length > 0 ? (
          <div>
            <h2
              className={classNames("font-semibold text-black mb-6", {
                "text-lg": isDesktop,
                "text-xl": !isDesktop
              })}
            >
              {translate("plans_included-features_heading")}
            </h2>
            <ul
              className={classNames("ul-1_check gap-x-10", {
                "columns-2": isDesktop
              })}
            >
              {selectedPlan.included_features.map(({ name }, index) => {
                return (
                  <li
                    key={`${name}${index}`}
                    className={"font-medium text-sm text-black"}
                  >
                    {name}
                  </li>
                );
              })}
            </ul>
          </div>
        ) : null}
        {selectedPlan?.not_included_features?.length ? (
          <div>
            <h2
              className={classNames("font-semibold text-black mb-6", {
                "text-lg": isDesktop,
                "text-xl": !isDesktop
              })}
            >
              {translate("plans_not-included-features_heading")}
            </h2>
            <ul
              className={classNames("ul-1_check gap-x-10", {
                "columns-2": isDesktop
              })}
            >
              {selectedPlan.not_included_features.map(({ name }, index) => {
                return (
                  <li
                    key={`${name}${index}`}
                    className={"li_x font-medium text-sm text-black"}
                  >
                    {name}
                  </li>
                );
              })}
            </ul>
          </div>
        ) : null}
      </section>
      <section
        className={classNames("plan-table-1 rounded bg-white mb-8 pb-2")}
      >
        <table>
          <thead>
            <tr>
              <th>
                <div
                  className={classNames(
                    "flex align-center border-bottom border-bottom-gray",
                    { "h-16.5": isDesktop, "h-13": !isDesktop }
                  )}
                >
                  <h2
                    className={classNames("text-black font-semibold mb-0", {
                      "text-2xl": isDesktop,
                      "text-lg": !isDesktop
                    })}
                  >
                    {translate("plans_compression_table_heading")}
                  </h2>
                </div>
              </th>
              {currentOffer.plans.map(({ name, currency_offers }, index) => {
                const currencyOffer = getCurrencyOfferByPeriod({
                  currency_offers,
                  period
                });

                return (
                  <th key={`${name}${index}`}>
                    <div
                      className={classNames(
                        "text-center flex flex-col gap justify-center border-bottom border-bottom-gray",
                        { "h-16.5": isDesktop, "h-13": !isDesktop }
                      )}
                    >
                      <h3
                        className={classNames("text-black font-semibold", {
                          "text-base": isDesktop,
                          "text-sm": !isDesktop
                        })}
                      >
                        {name}
                      </h3>
                      {currencyOffer ? (
                        <div className={"text-0"}>
                          <span
                            className={classNames(
                              "font-semibold text-black-6",
                              { "text-xs": isDesktop, "text-xs.1": !isDesktop }
                            )}
                          >
                            <FormatCurrency
                              locale={locale}
                              value={currencyOffer.offer_amount}
                            />{" "}
                          </span>
                          <span
                            className={classNames("font-medium text-black-6", {
                              "text-xs": isDesktop,
                              "text-xs.1": !isDesktop
                            })}
                          >
                            <Period periods={currencyOffer.periods} />
                          </span>
                        </div>
                      ) : null}
                    </div>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {sortedFeaturesByIncludedFeaturesCount({ currentOffer }).map(
              ({
                name,
                id: featureId,
                feature_tooltip_text,
                plan_tooltip_text
              }) => {
                const showTitleTooltip =
                  feature_tooltip_text && feature_tooltip_text.length > 0;

                const titleContent = (
                  <div className={"flex align-center gap-2"}>
                    {name}
                    {showTitleTooltip ? <Info size={10} /> : null}
                  </div>
                );

                return (
                  <tr key={featureId}>
                    <th className={"text-xs font-medium text-black"}>
                      {showTitleTooltip ? (
                        <SquaberTooltip title={feature_tooltip_text}>
                          {titleContent}
                        </SquaberTooltip>
                      ) : (
                        titleContent
                      )}
                    </th>
                    {currentOffer.plans.map(
                      ({ included_features, id: planId }) => {
                        const hasFeature = included_features.find(
                          ({ id: planFeatureId }) => {
                            return planFeatureId === featureId;
                          }
                        );

                        const showTooltip =
                          plan_tooltip_text && plan_tooltip_text.length > 0;

                        const content = (
                          <div
                            className={classNames(
                              "flex align-center justify-center",
                              {
                                "gap-2 ml-4.5": isDesktop,
                                "gap-1.5 ml-3.5": !isDesktop
                              }
                            )}
                          >
                            {hasFeature ? (
                              <img
                                src={PlanTableCheck}
                                width={16}
                                height={16}
                                alt={translate("yes")}
                              />
                            ) : (
                              <img
                                src={PlanTableX}
                                width={16}
                                height={16}
                                alt={translate("no")}
                              />
                            )}
                            {showTooltip ? (
                              <Info size={isDesktop ? 10 : 8} />
                            ) : (
                              <div style={{ width: isDesktop ? 10 : 8 }} />
                            )}
                          </div>
                        );

                        return (
                          <td key={planId}>
                            {showTooltip ? (
                              <SquaberTooltip title={feature_tooltip_text}>
                                {content}
                              </SquaberTooltip>
                            ) : (
                              content
                            )}
                          </td>
                        );
                      }
                    )}
                  </tr>
                );
              }
            )}
          </tbody>
        </table>
      </section>
      <section>
        <PlanTable2 />
      </section>
      {selectedPlan && currentCurrencyOfferByPeriod
        ? createPortal(
            <PaymentBarWrapper
              period={period}
              supportedPaymentMethods={supportedPaymentMethods}
              currentCurrencyOfferByPeriod={currentCurrencyOfferByPeriod}
              selectedPaymentMethod={selectedPaymentMethod}
              setSelectedPaymentMethod={setSelectedPaymentMethod}
              selectedPlan={selectedPlan}
              finalizePayment={finalizePayment}
              paymentFormLoading={paymentFormLoading}
              paymentDetails={paymentDetails}
              paymentDetailsLoading={paymentDetailsLoading}
            />,
            document.body
          )
        : null}
    </LandingTypography>
  );
};

export default compose(
  withUserData,
  withPlans,
  withRouter,
  withPaymentDetails
)(Plans);
